package com.ives.components.workspace;

import js.Browser;
import js.html.SpanElement;
import haxe.Timer;
import js.html.ProgressEvent;
import js.html.DragEvent;

import com.er453r.hxcompo.Component;

import com.ives.modules.cine.Cine;
import com.ives.modules.info.Info;
import com.ives.components.viewer.DicomViewer;
import com.ives.modules.Module;
import com.ives.modules.ModuleEvent;
import com.ives.components.image.DicomImage;
import com.ives.utils.Remoting;
import com.ives.utils.Remoting.DicomImageData;
import com.ives.utils.Remoting.ContextStatus;
import com.ives.utils.Log;
import com.ives.components.workspace.OrientationSide;
import com.ives.modules.tags.Tags;
import com.ives.utils.math.Vector3D;

import com.ives.modules.compare.Compare;
@:allow(com.ives.modules.Module)
@:allow(com.ives.components.viewer.DicomViewer)
class DicomWorkspace extends Component {
	public static inline var WORKSPACE_READY:String = "WORKSPACE_READY";

	private static inline var CONTEXT_LOADING:String = "LOADING";
	private static inline var CONTEXT_LOADED:String = "LOADED";
	private static inline var CONTEXT_ERROR:String = "ERROR";

	private var dicomImage:DicomImage = new DicomImage();

	private var id:UInt; // workspace id from all active workspaces
	private var mode:String; // current workspace mode - set by modules
	private var modules:Array<Module>; // modules for this workspace

	private var index:Int = 0;

	private var dicomViewer:DicomViewer;
	public var dicomImageData:DicomImageData;
	private var dicomContextData:DicomContextData;

	public var selected:Bool = false;

	private static var SIDES = [
		new OrientationSide(new Vector3D( 1, 0, 0), "orientation.left"),
		new OrientationSide(new Vector3D(-1, 0, 0), "orientation.right"),
		new OrientationSide(new Vector3D(0,  1, 0), "orientation.posterior"), // BACK
		new OrientationSide(new Vector3D(0, -1, 0), "orientation.anterior"), // FRONT
		new OrientationSide(new Vector3D(0, 0,  1), "orientation.superior"),
		new OrientationSide(new Vector3D(0, 0, -1), "orientation.inferior")
	];

	private static inline var SINGLE_SIDE_LIMIT = 0.414; // tan 22,5 degrees (1/16 full angle)

	public function new(id:UInt, modules:Array<Module>, dicomViewer:DicomViewer, viewerModuleEventHandler:ModuleEvent->Void, mode:String, dicomImageData:DicomImageData){
		Log.debug('Created workspace with id ${id}');

		on("loading", onLoading);
		on("progress", onProgress);
		on(DicomImage.IMAGE_LOADED, onLoad);
		on("mousedown", select);
		on("touchstart", select);
		on("dragenter", dragenter);
		on("dragover", dragenter);
		on("dragleave", dragleave);
		on("drop", drop);

		// init constructor arguments
		this.id = id;
		this.modules = modules;
		this.dicomViewer = dicomViewer;
		this.mode = mode;
		this.dicomImageData = this.dicomImage.dicomImageData = dicomImageData;

		if(dicomImageData.photometricInterpretation == null || dicomImageData.photometricInterpretation.length == 0){
			noImageDataWarning.style.display = "block";
			sliceInfo.style.display = "none";

			noImageDataWarning.addEventListener("click", function(){
				Log.info('Opening tags through warning ${id}');

				dispatch("moduleevent", new ModuleEvent(Tags.TAGS_MODULE));
			});
		}

		append(dicomImage);

		on(closeButton, "click", onCompareClose);

		for(module in modules){
			module.init(dicomViewer, this);

			append(module.viewElement);
		}

		initFromData(dicomImageData);
	}

	private function onLoading(){
		progressInfo.classList.add("show");
		progressLossless.style.width =  "0%";
		progressLossy.style.width = dicomImage.lossless ? "100%" : "0%";
	}

	private function onProgress(event:ProgressEvent){
		if(!event.lengthComputable)
			return;

		var progress:Float = 100 * event.loaded / event.total;

		if(dicomImage.lossless)
			progressLossless.style.width =  '${progress}%';
		else
			progressLossy.style.width =  '${progress}%';
	}

	private function onLoad(){
		progressInfo.classList.remove("show");

		updateOrientationInfo();

		onEventFromViewer(new ModuleEvent(DicomViewer.RESIZE, this.id));
	}

	// event from viewer - pass to modules
	private function onEventFromViewer(event:ModuleEvent){
		if(event.workspaceId != -1 && event.workspaceId != id) // handle only event for this workspace
			return;

		if(event.type == "CLOSE_OTHER" && event.strings[0] == dicomImageData.sourceName && event.strings[1] == dicomImageData.id)
			initFromData(dicomViewer.originalDicomImageData);

		for(module in modules)
			module.handleModuleEvent(event);
	}

	public function initFromData(dicomImageData:DicomImageData){
		this.dicomImage.dicomImageData = this.dicomImageData = dicomImageData;

		if(dicomImageData.id != dicomViewer.originalDicomImageData.id && id == 1 && dicomViewer.workspaces.length == 2)
			closeButton.style.display = "block";

		onLoading();

		Remoting.getContextStatus(dicomImageData.sourceName, dicomImageData.id, statusResult);
	}

	private function statusResult(status:ContextStatus){
		Log.debug('Context status ${status.loaded}/${status.total}  - ${status.status}');

		if(status.status == CONTEXT_LOADED){
			Remoting.contextData(dicomImageData.sourceName, dicomImageData.id, contextDataResult);
		}
		else if(status.status == CONTEXT_LOADING){
			var progress:Float = 100 * status.loaded / status.total;

			progressLossy.style.width =  '${progress}%';

			Timer.delay(function(){
				Remoting.getContextStatus(dicomImageData.sourceName, dicomImageData.id, statusResult);
			}, 500);
		}
		else{
			Log.debug('Context error!');
		}
	}

	private function contextDataResult(contextData:DicomContextData){
		this.dicomContextData = contextData;

		progressInfo.classList.remove("show");

		onEventFromViewer(new ModuleEvent(Info.INFO_UPDATE));

		if(mode == DicomViewer.WORKSPACE_NORMAL)
			onEventFromViewer(new ModuleEvent(Cine.CINE_MODULE));

		onEventFromViewer(new ModuleEvent(WORKSPACE_READY, this.id));
	}

	public function select(){
		for(workspace in dicomViewer.workspaces)
			workspace.deselect();

		selected = true;

		if(dicomViewer.workspaces.length > 1)
			viewElement.classList.add("selected");
	}

	public function deselect(){
		selected = false;

		viewElement.classList.remove("selected");
	}

	private function onCompareClose(){
		dispatch("moduleevent", new ModuleEvent(DicomViewer.LAYOUT_CHANGE, [1, 1], [DicomViewer.WORKSPACE_NORMAL], false, true));
	}

	private function updateOrientationInfo(){
		Log.debug("Updating orientation info");

		var sides = SIDES.copy();

		if(dicomImage.rowDirection == null || dicomImage.columnDirection == null){
			sliceOrientationTop.style.display = "none";
			sliceOrientationRight.style.display = "none";
			sliceOrientationBottom.style.display = "none";
			sliceOrientationLeft.style.display = "none";

			sliceOrientationMissing.style.display = "block";

			return;
		}
		else{
			sliceOrientationTop.style.display = "block";
			sliceOrientationRight.style.display = "block";
			sliceOrientationBottom.style.display = "block";
			sliceOrientationLeft.style.display = "block";
		}

		var labels = [
			new OrientationSideLabel(sliceOrientationTop, dicomImage.rowDirection.clone().scale(-1)),
			new OrientationSideLabel(sliceOrientationRight, dicomImage.columnDirection.clone()),
			new OrientationSideLabel(sliceOrientationBottom, dicomImage.rowDirection.clone()),
			new OrientationSideLabel(sliceOrientationLeft, dicomImage.columnDirection.clone().scale(-1)),
		];

		for(label in labels){
			sides.sort(function(x, y){return x.position.distanceFrom(label.position) - y.position.distanceFrom(label.position) > 0 ? 1 : -1;});

			while(label.element.firstChild != null)
				label.element.removeChild(label.element.firstChild);

			var span:SpanElement = Browser.document.createSpanElement();
			span.setAttribute("data-tr-title", sides[0].label);
			span.setAttribute("data-tr", '${sides[0].label}_short');

			label.element.appendChild(span);

			if(sides[0].position.distanceFrom(label.position) > SINGLE_SIDE_LIMIT){
				span = Browser.document.createSpanElement();
				span.setAttribute("data-tr-title", sides[1].label);
				span.setAttribute("data-tr", '${sides[1].label}_short');

				label.element.appendChild(span);
			}
		}
	}

	private function dragenter(event:DragEvent){
		Log.info("Drag ENTER" + event.dataTransfer.types);

		if(event.dataTransfer.types.length == 2){
			event.preventDefault();

			this.viewElement.classList.add("selected");
		}
	}

	private function dragleave(event:DragEvent){
		Log.info("Drag LEAVE " + event);

		if(event.dataTransfer.types.length == 2){
			this.viewElement.classList.remove("selected");
		}
	}

	private function drop(event:DragEvent){
		Log.info("Drag DROP " + event);

		var id:String = event.dataTransfer.getData("id");
		var source:String = event.dataTransfer.getData("source");

		dispatch("moduleevent", new ModuleEvent(Compare.COMPARE_EVENT, [this.id], [source, id], false ,true));
	}
}
